import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  DataTable,
  Spinner,
  TextField,
  ProgressBar,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface PickerStats {
  user_id: string | null;
  picker_name: string;
  lists_completed: number;
  total_items_picked: number;
  items_actually_picked: number;
  orders_picked: number;
  avg_pick_time_minutes: number;
  items_per_hour: number;
  accuracy_rate: number;
}

interface DailyPerformance {
  date: string;
  lists_completed: number;
  items_picked: number;
  orders_picked: number;
  active_pickers: number;
}

interface Summary {
  total_lists: number;
  total_items: number;
  total_orders: number;
  total_pickers: number;
  avg_list_time_minutes: number;
}

export default function PickerProductivityReport() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();
  const [pickers, setPickers] = useState<PickerStats[]>([]);
  const [dailyPerformance, setDailyPerformance] = useState<DailyPerformance[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const loadReport = useCallback(async () => {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    const result = await fetchApi<{
      success: boolean;
      pickers: PickerStats[];
      daily_performance: DailyPerformance[];
      summary: Summary;
    }>(`/reports/picker-productivity?${params.toString()}`);

    if (result?.success) {
      setPickers(result.pickers || []);
      setDailyPerformance(result.daily_performance || []);
      setSummary(result.summary);
    }
  }, [fetchApi, startDate, endDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await fetchApi<{ success: boolean; download_url: string }>('/reports/picker-productivity/export', {
        method: 'POST',
        body: JSON.stringify({ start_date: startDate, end_date: endDate }),
      });

      if (result?.success && result.download_url) {
        window.open(`${import.meta.env.VITE_API_URL}${result.download_url}`, '_blank');
      }
    } finally {
      setExporting(false);
    }
  };

  const formatTime = (minutes: number | null) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const getPerformanceBadge = (itemsPerHour: number) => {
    if (itemsPerHour >= 60) return <Badge tone="success">Excellent</Badge>;
    if (itemsPerHour >= 40) return <Badge tone="info">Good</Badge>;
    if (itemsPerHour >= 20) return <Badge tone="warning">Average</Badge>;
    return <Badge tone="critical">Below Target</Badge>;
  };

  const pickerRows = pickers.map((picker, index) => [
    <InlineStack gap="200" align="center">
      <Badge tone={index === 0 ? 'success' : undefined}>{String(index + 1)}</Badge>
      <Text as="span" fontWeight="bold">{picker.picker_name}</Text>
    </InlineStack>,
    String(picker.lists_completed),
    String(picker.items_actually_picked),
    String(picker.orders_picked),
    formatTime(picker.avg_pick_time_minutes),
    <Text as="span" fontWeight="bold">{picker.items_per_hour?.toFixed(1) || '0'}</Text>,
    <InlineStack gap="200" align="center">
      <div style={{ width: 60 }}>
        <ProgressBar progress={picker.accuracy_rate || 0} tone="primary" size="small" />
      </div>
      <Text as="span" variant="bodySm">{picker.accuracy_rate?.toFixed(1) || 0}%</Text>
    </InlineStack>,
    getPerformanceBadge(picker.items_per_hour || 0),
  ]);

  const dailyRows = dailyPerformance.slice(0, 14).map((day) => [
    new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
    String(day.lists_completed),
    String(day.items_picked),
    String(day.orders_picked),
    String(day.active_pickers),
    day.active_pickers > 0 ? Math.round(day.items_picked / day.active_pickers).toString() : '-',
  ]);

  return (
    <Page
      title="Picker Productivity Report"
      subtitle="Track picker performance and warehouse efficiency"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
      primaryAction={{
        content: 'Export CSV',
        onAction: handleExport,
        loading: exporting,
        disabled: pickers.length === 0,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        {summary && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Total Items Picked</Text>
                  <Text as="p" variant="heading2xl">{summary.total_items?.toLocaleString() || 0}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Pick Lists Completed</Text>
                  <Text as="p" variant="heading2xl">{summary.total_lists || 0}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Orders Fulfilled</Text>
                  <Text as="p" variant="heading2xl">{summary.total_orders || 0}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Avg List Time</Text>
                  <Text as="p" variant="heading2xl">{formatTime(summary.avg_list_time_minutes)}</Text>
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="start" wrap>
              <div style={{ width: '150px' }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={setStartDate}
                  autoComplete="off"
                />
              </div>
              <div style={{ width: '150px' }}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={setEndDate}
                  autoComplete="off"
                />
              </div>
              <div style={{ paddingTop: '24px' }}>
                <Button onClick={loadReport}>Refresh</Button>
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Picker Leaderboard */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Picker Leaderboard ({pickers.length} pickers)
              </Text>

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spinner />
                </div>
              ) : pickers.length === 0 ? (
                <Banner tone="info">
                  No picking activity found for the selected period.
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'text', 'numeric', 'text', 'text']}
                  headings={['Picker', 'Lists', 'Items', 'Orders', 'Avg Time', 'Items/Hr', 'Accuracy', 'Performance']}
                  rows={pickerRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Daily Performance */}
        {dailyPerformance.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Daily Performance (Last 14 Days)
                </Text>

                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                  headings={['Date', 'Lists', 'Items', 'Orders', 'Pickers', 'Avg Items/Picker']}
                  rows={dailyRows}
                />
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Performance Benchmarks */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">Performance Benchmarks</Text>
              <InlineStack gap="600">
                <InlineStack gap="200">
                  <Badge tone="success">Excellent</Badge>
                  <Text as="span" variant="bodySm">60+ items/hour</Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Badge tone="info">Good</Badge>
                  <Text as="span" variant="bodySm">40-59 items/hour</Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Badge tone="warning">Average</Badge>
                  <Text as="span" variant="bodySm">20-39 items/hour</Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Badge tone="critical">Below Target</Badge>
                  <Text as="span" variant="bodySm">&lt;20 items/hour</Text>
                </InlineStack>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
